import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Socket from '../../connection/socket';
import { ConnectedUser } from '../../stores/types/connection';
import useShots from '../../contexts/shots/provider';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { ROOM_STATES } from '../../stores/reducers/settings';
import type { ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import type { DragItem } from '../../stores/types/user-interface';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import { GreenroomState as GS } from '../../stores/types/types';
import Icon, { COLORS, ICONS } from '../general-components/icon';
import {
	VID_TYPES, IMG_TYPES, DOC_TYPES, AUDIO_TYPES,
} from '../../types/constants';
import FeedThumbnail from '../general-components/feed-thumbnail';
import type { MediaItem } from '../../stores/types/media';
import AvatarBubbleRow from '../general-components/avatar-bubble-row';
import { isVideo } from '../../utils/utils';
import { setSelectedMediaTemplate } from '../../stores/actions/generate-content';
import { setActiveSubTab, setActiveTab, setRightDrawerOpen } from '../../stores/actions/tabs';
import { TABS } from '../../stores/types/tabs';
import useProfile from '../../hooks/use-profile';
import useMediaItemMemos from '../../hooks/use-media-memos';
import { MEDIA_SETTINGS_TABS } from '../../stores/types/session';

const allowedAudioOnlyFeedTypes = [
	FEED_TYPES.MEDIA,
	FEED_TYPES.PRESENTER,
	FEED_TYPES.EMAIL,
	DRAG_TYPE.OVERLAY,
	FEED_TYPES.SCREENSHARE,
	FEED_TYPES.MEDIA_AUDIO,
	FEED_TYPES.SIP_CALL,
	FEED_TYPES.SRT_STREAM,
	FEED_TYPES.ONSITE_LOCATION,
];

interface AudioOnlyFeedsProps {
	isForShotEditorModal: boolean;
	socket: Socket | null;
}

export default function AudioOnlyFeeds(audioOnlyFeedsProps: AudioOnlyFeedsProps) {
	const dispatch = useDispatch();
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const modalOpen = useSelector((state: GS) => state.ModalReducer.modalOpen);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const roomState = useSelector((state: GS) => state.MeetingReducer.roomState);
	const globalIsDragging = useSelector((state: GS) => state.UserInterfaceReducer.globalIsDragging);
	const typeOfDragging = useSelector((state: GS) => state.UserInterfaceReducer.typeOfDragging);
	const idOfDragging = useSelector((state: GS) => state.UserInterfaceReducer.idOfDragging);
	const invitations = useSelector((state: GS) => state.InvitationsReducer.invitations);
	const media = useSelector((state: GS) => state.MediaReducer.media);
	const connectedUsers = useSelector((state: GS) => state.SessionReducer.connectedUsers);
	const shotModalShot = useSelector((state: GS) => state.ShotsReducer.shotModalShot);
	const presenterVideos = useSelector((state: GS) => state.VideoReducer.connectedPresenterVideos);
	const srtStreams = useSelector((state: GS) => state.VideoReducer.srtStreams);
	const generatedContent = useSelector((s: GS) => s.GeneratedContentReducer.generatedContent);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const selectedMediaItem = useSelector((state: GS) => state.GeneratedContentReducer.selectedMediaItem);
	const {activeTab, rightDrawerOpen} = useSelector((state: GS) => state.TabsReducer);
	const onsiteLocations = useSelector((state: GS) => state.VideoReducer.onsiteLocations);
	const { use_title_library: useTitleLibrary } = useSelector((state: GS) => state.SettingsReducer.featureFlags);

	const [showButton, setShowButton] = useState<boolean>(false);
	const [showDrawer, setShowDrawer] = useState<boolean>(false);
	const [isHovering, setIsHovering] = useState<boolean>(false);
	const [alignWithVideoButtons, setAlignWithVideoButtons] = useState<boolean>(false);
	const [audioOnlyShotFeeds, setAudioOnlyShotFeeds] = useState<ShotFeed[]>([]);
	const [useableMedia, setUseableMedia] = useState<MediaItem[]>([]);
	const [useableFeeds, setUseableFeeds] = useState<ConnectedUser[]>([]);
	const [processDelete, setProcessDelete] = useState<boolean>(false);
	const [visibleCanvasShotFeeds, setVisibleCanvasShotFeeds] = useState<ShotFeed[]>();

	const {
		currentShot,
		feedDroppedOnCanvas,
		removeFeedFromShot,
	} = useShots();

    const {
        currentTemplate,
        currentTemplateMediaItem,
        currentAudioMediaItem,
    } = useMediaItemMemos();

	const {
		getMaskedProfileById,
		getMaskFromEmail
	} = useProfile({});

	const resetUI = () => {
		setShowDrawer(false);
		setShowButton(false);
	};

	const dropCallback = (item: DragItem) => {
		if (!token || Number.isNaN(item.id)) {
			return;
		}

		feedDroppedOnCanvas(
			item,
			audioOnlyFeedsProps.socket,
			audioOnlyFeedsProps.isForShotEditorModal,
			0,
			true,
		);
	};
	const toggleDrawer = () => {
		if (audioOnlyShotFeeds.length > 0) setShowDrawer(!showDrawer);
		else {
			setShowDrawer(false);
			setShowButton(!showButton);
		}
	};

	const { isOver, dropRef } = useDropArea({
		accepts: allowedAudioOnlyFeedTypes,
		dropCallback,
		dropAreaData: {},
	});

	const removeAudioOnlyItem = (item: ShotFeed) => {
		if (audioOnlyFeedsProps.socket === null) {
			throw new Error('No socket, can\'t remove audio only item from list');
		}
		setProcessDelete(true);
		removeFeedFromShot(item, audioOnlyFeedsProps.socket, audioOnlyFeedsProps.isForShotEditorModal);
		if (audioOnlyShotFeeds.length === 1)
			resetUI();

		// Close the menu if it is currently open to edit this item
		if (rightDrawerOpen && (
			 (activeTab == TABS.AUDIO_SETTINGS && selectedMediaItem?.id == item.media) ||
			 (activeTab == TABS.GENERATE_MEDIA_CONTENT && (item.feedType == FEED_TYPES.MEDIA || item.feedType == FEED_TYPES.MEDIA_AUDIO)))) {
			dispatch(setRightDrawerOpen(false));
		}
	};

	const editAudioOnlyItem = (item: ShotFeed) => {
		if (currentTemplate && currentTemplateMediaItem) {
			dispatch(setSelectedMediaTemplate(currentTemplateMediaItem, currentTemplateMediaItem.template_type ?? ''));
			dispatch(setRightDrawerOpen(true));
			dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
			dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, MEDIA_SETTINGS_TABS.AUDIO));
		} else {
			const feed = currentShot?.shot_data.shotFeeds.find((x) => x.feedType == FEED_TYPES.MEDIA_AUDIO);
			if (feed) {
				const mediaItem = media.find((m) => m.id === feed.media);
				if (mediaItem) {
					dispatch(setSelectedMediaTemplate(mediaItem, mediaItem.type));
					dispatch(setRightDrawerOpen(true));
					dispatch(setActiveTab(TABS.AUDIO_SETTINGS));
				}
			}
		}
		toggleDrawer();
	}

	useEffect(() => {
		setShowButton(!!globalIsDragging
			&& (modalOpen === audioOnlyFeedsProps.isForShotEditorModal)
			&& (allowedAudioOnlyFeedTypes
				.find((allowedAudioOnlyFeedType) => allowedAudioOnlyFeedType === typeOfDragging)
				!== undefined));
	}, [globalIsDragging, modalOpen, audioOnlyFeedsProps.isForShotEditorModal, typeOfDragging]);

	useEffect(() => {
		setAlignWithVideoButtons(
			(roomState === ROOM_STATES.EDITING && host) || audioOnlyFeedsProps.isForShotEditorModal,
		);
	}, [roomState, audioOnlyFeedsProps.isForShotEditorModal]);

	useEffect(() => {
		const feedsSource = audioOnlyFeedsProps.isForShotEditorModal
			? shotModalShot
			: currentShot;

		// We ignore the empty feeds here and we will remove them in the save function
		setVisibleCanvasShotFeeds(() => feedsSource.shot_data.shotFeeds
			.filter((feed: ShotFeed) => isVideo(media.find((med) => med.id === feed.media)?.type || '') && !feed.audioOnly));

		setAudioOnlyShotFeeds(() => feedsSource.shot_data.shotFeeds
			.filter((feed: ShotFeed) => feed.audioOnly === true && feed.feedType !== FEED_TYPES.EMPTY));
	}, [
		shotModalShot.shot_data.shotFeeds,
		audioOnlyFeedsProps.isForShotEditorModal,
		currentShot.shot_data.shotFeeds,
		media,
	]);

	// want to reset the UI when the user changes shots or the feed drawer hits 0 items
	useEffect(() => resetUI(), [currentShot.shot_id]);
	// reset the UI when the AOF drawer is closed
	useEffect(() => {
		if (audioOnlyShotFeeds.length === 0) resetUI();
	}, [audioOnlyShotFeeds]);

	useEffect(() => {
		// Will update the audio only after delete, clear any processing state
		setProcessDelete(false);

		setUseableMedia(() => media.filter((mediaItem) => audioOnlyShotFeeds
			.some((s) => s?.media === mediaItem.id)));

		setUseableFeeds(() => connectedUsers
			.filter((user) => audioOnlyShotFeeds.some((s) => s?.presenter === user.gr_user)));
	}, [audioOnlyShotFeeds, connectedUsers]);

	const getMediaType = (feed: ShotFeed) => {
		if (feed.feedType === 'media' || feed.feedType === 'audio') {
			const mediaItem = useableMedia.find((m) => m.id === feed.media);
			if (VID_TYPES.some((t) => t === mediaItem?.type)) return 'video';
			// Need to add music type here when we have it
			if (mediaItem?.type === 'hls') return 'recording';
			if (AUDIO_TYPES.includes(mediaItem?.type ?? '')) return 'audio';
		} else if (feed.feedType === 'screenshare') {
			return 'screenshare';
		}
		return 'Live Feed';
	};

	const findScreenshareUser = (feed: ShotFeed) => connectedUsers
		.find((u) => u.email === feed.screenshare)
				|| invitations.find((i) => i.profile.email === feed.screenshare)?.profile
				|| feed.screenshare;

	const canDropFeedCheck = () => {
		const audioOnlyVideoNumber = audioOnlyShotFeeds
			.reduce((previousValue:number, currentValue:ShotFeed) => {
				const singleMedia = media.find((elm) => elm.id === currentValue.media);
				let videoCheck = false;
				if (singleMedia) {
					videoCheck = isVideo(singleMedia.type);
				}
				return previousValue + (videoCheck ? 1 : 0);
			}, 0);

		const totalVideosOnCanvas = visibleCanvasShotFeeds
			? visibleCanvasShotFeeds.length + audioOnlyVideoNumber
			: audioOnlyVideoNumber;

		const draggingIsInFeed = !!audioOnlyShotFeeds.find(
			(med) => med.media === idOfDragging,
		) || visibleCanvasShotFeeds?.find((med) => med.media === idOfDragging);
		const draggingIsInAudioFeeds = !!audioOnlyShotFeeds.find(
			(med) => med.media === idOfDragging
			|| med.presenter === idOfDragging
			|| med.srtStreamId === idOfDragging
			|| med.onsiteLocationId === idOfDragging,
		);

		let draggingType = media.find((med) => med.id === idOfDragging)?.type;

		if (!draggingType && typeOfDragging === 'media' && typeof idOfDragging === 'string') {
			draggingType = idOfDragging;
		}

		const isVideoType = isVideo(draggingType || '');
		let isOfCorrectType = true;
		if (IMG_TYPES.some((t) => t === draggingType)
			|| DOC_TYPES.some((t) => t === draggingType)
			|| typeOfDragging === 'overlay'
			|| typeOfDragging === 'title') {
			isOfCorrectType = false;
		}

		return {
			canDropFeed: () => {
				if (audioOnlyShotFeeds.length < 8 && isOfCorrectType) {
					if (totalVideosOnCanvas >= 8 && !draggingIsInFeed && isVideoType) return false;
					if (draggingIsInAudioFeeds) return false;
					return true;
				}
				return false;
			},
			why: () => {
				if (!isOfCorrectType) {
					return 'File Type Not Allowed';
				} if (draggingIsInAudioFeeds) {
					return 'Item Already In Audio Feeds';
				} if (
					totalVideosOnCanvas >= 8
					&& draggingType
					&& isVideoType
					&& !draggingIsInFeed
				) {
					return 'Maximum of 8 Videos on Canvas';
				}
				return 'Maximum of 8 Audio Feeds Reached';
			},
		};
	};

	const dragButton = () => <>
		{canDropFeedCheck().canDropFeed()
			? <div
				ref={dropRef}
				onClick={toggleDrawer}
				className={classNames('audio-only-feeds-drag-button', { invisible: !showButton })}
			>
				<span className='button-text' data-testid="drag-button-text">
					{!host && !audioOnlyShotFeeds.length ? 'No Audio Only Feeds Are Placed' : 'Drop here to add Audio Feeds'}
				</span>
			</div>
			: globalIsDragging && <button className={'audio-only-feeds-drop-area-disabled font-weight-normal position-bottom white-space-nowrap'}>
			<span className={'audio-only-feeds-drop-area-disabled-span font-weight-normal white-space-nowrap'} data-testid="drag-button-error-text">
				{canDropFeedCheck().why()}
			</span>
		</button>}
	</>;

	const getMediaName = (feed: ShotFeed) => {
		if (feed.feedType === 'media' || feed.feedType === 'audio') {
			const mediaItem = useableMedia.find((m) => m.id === feed.media);
			return mediaItem?.custom_name ?? mediaItem?.original_name;
		}

		if (feed.feedType === 'presenter') {
			const connUser = useableFeeds.find((u) => u.gr_user === feed.presenter);
			const maskedUser = feed.presenter ? getMaskedProfileById(feed.presenter) : {};
			const connectedUser = {...connUser, ...maskedUser}
			if (connectedUser) {
				if (connectedUser.first_name || connectedUser.last_name) {
					return `${connectedUser?.first_name} ${connectedUser?.last_name}`;
				}
				return connectedUser?.email;
			}
			const invitedUser = invitations
				.find((i) => i.profile.gr_user === feed?.presenter);
			if (invitedUser?.profile.first_name || invitedUser?.profile.last_name) {
				return `${invitedUser?.profile.first_name} ${invitedUser?.profile.last_name}`;
			} if (invitedUser?.profile.email) {
				return invitedUser?.profile.email;
			}

			return 'User Placeholder';
		}

		if (feed.feedType === 'email') {
			const invitedUserNotInRoom = invitations
				.find((i) => i.profile.email === feed?.email);

			const mask = feed.email ? getMaskFromEmail(feed.email) : null;

			if (mask && (mask.first_name || mask.last_name)) {
				return `${mask.first_name} ${mask.last_name}`;
			}

			return invitedUserNotInRoom?.profile.email;
		}

		if (feed.feedType === 'screenshare') {
			let username: string;
			const user = findScreenshareUser(feed);

			if (typeof user !== 'string' && (user?.first_name || user?.last_name || user?.email)) {
				// invitation or connected user
				username = user?.first_name || user?.last_name ? `${user.first_name} ${user.last_name}` : user.email;
			} else {
				// it's just the email address and I think this is a rare case
				username = user as string | null ?? 'User Placeholder';
			}

			return username;
		}

		if (feed.feedType === 'sipCall') {
			const connectedPresenterVideo = presenterVideos.find((vid) => vid.id === feed.sipStreamId);
			if (connectedPresenterVideo) {
				return connectedPresenterVideo.displayName;
			}
		}

		if (feed.feedType === 'srtStream') {
			const srtStream = srtStreams.find((s) => s.id === feed.srtStreamId);
			if (srtStream) {
				return `SRT Stream: ${srtStream.name}`;
			}
		}

		if (feed.feedType === 'onsiteLocation') {
			const onsiteLocation = onsiteLocations.find((s) => s.id === feed.onsiteLocationId);
			if (onsiteLocation) {
				return onsiteLocation.locationName;
			}
		}

		return 'Unknown';
	};

	const constructAvatarArray = (shotFeeds: ShotFeed[]) => {
		const avatarArray = shotFeeds.map((feed) => {
			if (feed.feedType === 'media') {
				const mediaType = getMediaType(feed);
				const icon = () => {
					if (mediaType === 'video') return ICONS.VIDEO;
					if (mediaType === 'recording') return ICONS.RECORDING;
					// Can use this when this media type exists
					// if (mediaType === 'music') return ICONS.MUSIC_NOTE;
					return ICONS.INFO_CIRCLE;
				};
				return {
					icon: icon(),
					type: mediaType,
				};
			}

			if (feed.feedType === 'presenter') {
				const connectedUser = useableFeeds.find((u) => u.gr_user === feed.presenter);
				if (connectedUser) return connectedUser;
				const invitedUser = invitations
					.find((i) => i.profile.gr_user === feed?.presenter);
				if (invitedUser) return invitedUser?.profile;

				return 'User Placeholder';
			}

			if (feed.feedType === 'email') {
				const invitedUserNotInRoom = invitations
					.find((i) => i.profile.email === feed?.email);

				return invitedUserNotInRoom?.profile;
			}

			if (feed.feedType === 'screenshare') {
				return {
					icon: ICONS.SCREENSHARE,
					type: 'screenshare',
				};
			}

			if (feed.feedType === 'audio') {
				return {
					icon: ICONS.MUSIC,
					type: 'audio',
				};
			}

			if (feed.feedType === 'srtStream') {
				return {
					icon: ICONS.CAMERA_ON,
					type: 'srt',
				};
			}

			if (feed.feedType === 'onsiteLocation') {
				return {
					icon: ICONS.LOCATION,
					type: 'location',
				};
			}

			return null;
		}).filter((a) => a !== null);

		return avatarArray;
	};

	const getDrawerHeight = useMemo(() => {
		// 59 is the height of the header, padding, and extra padding on the first-child
		// and 60 is the ~height of each item in the feed list
		const height = audioOnlyShotFeeds.length * 60 + 59;
		// max-height we allow is 300px
		return `${height > 300 ? 300 : height}px`;
	}, [audioOnlyShotFeeds.length]);

	const displayedText = (audioOnlyShotFeeds[0] && audioOnlyShotFeeds.length === 1)
		? getMediaName(audioOnlyShotFeeds[0])
		: 'Audio only';

	return (
		<div className="audio-only-feeds-wrapper">
			<div
				style={{ height: !showButton && showDrawer ? getDrawerHeight : '80px' }}
				data-testid="main-feeds-container"
				className={classNames(
					'main-feeds-container',
					'font-weight-normal',
					showButton ? {
						visible: true,
						button: true,
						'audio-only-feeds-drop-area': true,
						isover: isOver,
						highlight: isOver,
						'border-large-dashed-green': isOver,
						'text-green': isOver,
						'border-large-dashed': !isOver,
						'text-gray': !isOver,
					} : {
						visible: (showDrawer && audioOnlyShotFeeds.length > 0),
						expanded: showDrawer,
					},
				)}
			>
				<div className={classNames('feed-container', { hidden: !showDrawer, fadeout: showButton })}>
					<div className="title-container">
						<p className='audio-only-feeds-title'>Audio Only Feeds</p>
						<button onClick={toggleDrawer} className="transparent text-highlight">
							<Icon name={ICONS.CLOSE} color={COLORS.DARK_GRAY} size={16} />
						</button>
					</div>
					<div className="feed-list overflow-scroll-y overflow-hidden-x" data-testid="feed-list">
						{audioOnlyShotFeeds.map((feed: ShotFeed, index) => (
							<div
								key={`audio-shot-feed-${index}`}
								className={classNames(
									'audio-only-item',
									{ audio: feed.feedType === 'audio' },
								)}
								data-testid="audio-only-feed"
							>
								<div className='thumbnail'>
									<div className='thumbnail-container'>
										<FeedThumbnail shotFeed={feed} />
									</div>
								</div>
								<div className="text-container">
									<span className='media-name font-weight-heavy'>{getMediaName(feed)}</span>
									<span className='media-type' data-testid="feed-type">{getMediaType(feed)}</span>
								</div>
								<div className="button-container">
									{feed.feedType === 'audio' && 
									 roomState === ROOM_STATES.EDITING && useTitleLibrary && (
										<button
											className="control-container edit-btn"
											data-testid="edit-audio-only-feed"
											onClick={() => editAudioOnlyItem(feed)}
										>
											<Icon name={ICONS.EDIT} color={COLORS.TEXT_LIGHT} size={16} />
										</button>
									)}
									<button
										disabled={processDelete}
										onClick={() => removeAudioOnlyItem(feed)}
										className={classNames('control-container delete-btn', { hidden: !host })}
										data-testid="delete-audio-only-feed"
									>
										<Icon name={ICONS.MEDIA_DELETE} color={COLORS.TEXT_LIGHT} size={16} />
									</button>
								</div>
							</div>
						))}
					</div>
				</div>
				{audioOnlyFeedsProps.isForShotEditorModal === modalOpen ? dragButton() : <></>}
			</div>
			<button
				onClick={toggleDrawer}
				data-testid="audio-only-feeds-button"
				className={classNames(
					'audio-only-feeds-button',
					'border-radius-large',
					'width-fit-content',
					alignWithVideoButtons ? 'background-grayscale-800' : 'background-light',
					{ avatars: audioOnlyShotFeeds.length > 0 },
				)}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
			>
				{audioOnlyShotFeeds.length === 0
					? <Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} />
					: <AvatarBubbleRow
						invites={constructAvatarArray(audioOnlyShotFeeds)}
						size={'twentyfour'} alignEnd={false}
						maxBubbles={3}
						canDisplayHoverIcon={isHovering}
					/>
				}
				<span className='font-weight-heavy text-white'>
					{displayedText}
				</span>
			</button>
		</div>
	);
}
